import type { DashboardView } from '../Common/MobileDashboardDock'

export type DashboardActionKey =
  | 'landscape'
  | 'fullscreen'
  | 'gridOverlay'
  | 'resetView'
  | 'collapseClusters'
  | 'jumpToday'
  | 'timeRange'
  | 'rotateAxis'
  | 'timeWindow'
  | 'followPath'
  | 'previousStep'
  | 'nextStep'
  | 'sort'
  | 'filter'
  | 'density'
  | 'range'
  | 'legend'
  | 'help'
  | 'shortcuts'

export type CanvasAxis = 'xy' | 'x' | 'y' | 'none'

export type ZoomMode = 'geometric' | 'semantic' | 'density' | 'none'

export type ViewCanvasDescriptor = {
  view: DashboardView
  description: string
  pan: CanvasAxis
  zoomMode: ZoomMode
  zoomAxis: CanvasAxis
  zoom?: {
    label: (zoomPercent: number, windowDays?: number) => string
  }
  replacementControl?: string
  pinch: boolean
  supportsSelection: boolean
  extras: readonly DashboardActionKey[]
  disabled?: boolean
  datePan: boolean
  horizontalScroll: boolean
  fitTimeline: boolean
  focusElement: boolean
  expand: boolean
  canvasPan: boolean
}

export const VIEW_CANVAS_DESCRIPTORS: Record<DashboardView, ViewCanvasDescriptor> = {
  Timeline: {
    view: 'Timeline',
    description: 'Gantt across months',
    pan: 'xy',
    zoomMode: 'semantic',
    zoomAxis: 'x',
    zoom: {
      label: (zoomPercent, windowDays) => {
        if (typeof windowDays === 'number') {
          if (windowDays <= 14) return '1 wk'
          if (windowDays <= 45) return '1 mo'
          if (windowDays <= 120) return '1 qtr'
          return '1 yr'
        }
        return zoomPercent >= 300 ? '1 mo' : zoomPercent >= 150 ? '1 qtr' : '1 yr'
      },
    },
    pinch: true,
    supportsSelection: true,
    extras: ['jumpToday', 'timeRange'],
    datePan: true,
    horizontalScroll: false,
    fitTimeline: true,
    focusElement: true,
    expand: true,
    canvasPan: true,
  },
  Constellation: {
    view: 'Constellation',
    description: 'Force-directed graph',
    pan: 'xy',
    zoomMode: 'geometric',
    zoomAxis: 'xy',
    zoom: { label: (zoomPercent) => `${zoomPercent}%` },
    pinch: true,
    supportsSelection: true,
    extras: ['resetView', 'collapseClusters', 'gridOverlay'],
    datePan: false,
    horizontalScroll: false,
    fitTimeline: true,
    focusElement: true,
    expand: true,
    canvasPan: true,
  },
  Radar: {
    view: 'Radar',
    description: 'Orbital status',
    pan: 'xy',
    zoomMode: 'semantic',
    zoomAxis: 'xy',
    zoom: {
      label: (zoomPercent, windowDays) => {
        if (typeof windowDays === 'number') {
          if (windowDays <= 10) return '1w'
          if (windowDays <= 45) return '1m'
          if (windowDays <= 120) return '1q'
          return '1y'
        }
        return zoomPercent >= 300 ? '1w' : zoomPercent >= 150 ? '1m' : zoomPercent >= 80 ? '1q' : '1y'
      },
    },
    pinch: true,
    supportsSelection: true,
    extras: ['rotateAxis', 'timeWindow'],
    datePan: false,
    horizontalScroll: false,
    fitTimeline: true,
    focusElement: true,
    expand: true,
    canvasPan: true,
  },
  Sandfall: {
    view: 'Sandfall',
    description: 'Time as gravity',
    pan: 'y',
    zoomMode: 'none',
    zoomAxis: 'none',
    pinch: false,
    supportsSelection: true,
    extras: ['jumpToday'],
    datePan: false,
    horizontalScroll: true,
    fitTimeline: false,
    focusElement: false,
    expand: true,
    canvasPan: true,
  },
  Journey: {
    view: 'Journey',
    description: 'Task-by-task path',
    pan: 'xy',
    zoomMode: 'geometric',
    zoomAxis: 'xy',
    zoom: { label: () => 'zoom' },
    pinch: true,
    supportsSelection: true,
    extras: ['previousStep', 'nextStep', 'followPath'],
    datePan: true,
    horizontalScroll: false,
    fitTimeline: true,
    focusElement: true,
    expand: true,
    canvasPan: true,
  },
  Grid: {
    view: 'Grid',
    description: 'Card matrix',
    pan: 'y',
    zoomMode: 'density',
    zoomAxis: 'none',
    replacementControl: 'md',
    pinch: false,
    supportsSelection: true,
    extras: ['sort', 'filter', 'density'],
    datePan: false,
    horizontalScroll: true,
    fitTimeline: false,
    focusElement: false,
    expand: true,
    canvasPan: true,
  },
  Heatmap: {
    view: 'Heatmap',
    description: 'Density by day',
    pan: 'xy',
    zoomMode: 'semantic',
    zoomAxis: 'x',
    replacementControl: '30 D',
    pinch: false,
    supportsSelection: true,
    extras: ['range', 'legend'],
    datePan: false,
    horizontalScroll: true,
    fitTimeline: false,
    focusElement: false,
    expand: true,
    canvasPan: false,
  },
  WarRoom: {
    view: 'WarRoom',
    description: 'Live team overlay',
    pan: 'none',
    zoomMode: 'none',
    zoomAxis: 'none',
    pinch: false,
    supportsSelection: false,
    extras: [],
    disabled: true,
    datePan: false,
    horizontalScroll: false,
    fitTimeline: false,
    focusElement: false,
    expand: true,
    canvasPan: true,
  },
  Stripboard: {
    view: 'Stripboard',
    description: 'Checklist matrix',
    pan: 'y',
    zoomMode: 'density',
    zoomAxis: 'none',
    replacementControl: 'md',
    pinch: false,
    supportsSelection: true,
    extras: ['sort', 'filter', 'density'],
    datePan: false,
    horizontalScroll: true,
    fitTimeline: false,
    focusElement: false,
    expand: true,
    canvasPan: true,
  },
  Leaderboard: {
    view: 'Leaderboard',
    description: 'Reliability ranking',
    pan: 'none',
    zoomMode: 'none',
    zoomAxis: 'none',
    pinch: false,
    supportsSelection: false,
    extras: [],
    datePan: false,
    horizontalScroll: false,
    fitTimeline: false,
    focusElement: false,
    expand: true,
    canvasPan: true,
  },
  Perimeter: {
    view: 'Perimeter',
    description: 'Due-date perimeter',
    pan: 'none',
    zoomMode: 'none',
    zoomAxis: 'none',
    pinch: false,
    supportsSelection: false,
    extras: [],
    datePan: false,
    horizontalScroll: false,
    fitTimeline: false,
    focusElement: false,
    expand: true,
    canvasPan: true,
  },
  LootDrop: {
    view: 'LootDrop',
    description: 'Win lanes',
    pan: 'x',
    zoomMode: 'none',
    zoomAxis: 'none',
    pinch: false,
    supportsSelection: false,
    extras: [],
    datePan: false,
    horizontalScroll: true,
    fitTimeline: false,
    focusElement: false,
    expand: true,
    canvasPan: true,
  },
  Flowband: {
    view: 'Flowband',
    description: 'Remaining weight bands',
    pan: 'xy',
    zoomMode: 'semantic',
    zoomAxis: 'x',
    pinch: false,
    supportsSelection: false,
    extras: [],
    datePan: true,
    horizontalScroll: false,
    fitTimeline: false,
    focusElement: false,
    expand: true,
    canvasPan: false,
  },
  Burnshore: {
    view: 'Burnshore',
    description: 'Weighted burndown',
    pan: 'xy',
    zoomMode: 'semantic',
    zoomAxis: 'x',
    pinch: false,
    supportsSelection: false,
    extras: [],
    datePan: true,
    horizontalScroll: false,
    fitTimeline: false,
    focusElement: false,
    expand: true,
    canvasPan: false,
  },
  Cadence: {
    view: 'Cadence',
    description: 'Completion cadence',
    pan: 'y',
    zoomMode: 'none',
    zoomAxis: 'none',
    pinch: false,
    supportsSelection: false,
    extras: [],
    datePan: true,
    horizontalScroll: false,
    fitTimeline: false,
    focusElement: false,
    expand: true,
    canvasPan: false,
  },
}

export const getViewDescriptor = (view: DashboardView): ViewCanvasDescriptor =>
  VIEW_CANVAS_DESCRIPTORS[view]

export const dashboardActionLabel = (action: DashboardActionKey, view: DashboardView): string => {
  switch (action) {
    case 'landscape':
      return 'Landscape mode'
    case 'fullscreen':
      return 'Fullscreen'
    case 'gridOverlay':
      return 'Grid overlay'
    case 'resetView':
      return 'Reset view'
    case 'collapseClusters':
      return 'Collapse clusters'
    case 'jumpToday':
      return 'Jump to today'
    case 'timeRange':
      return 'Time-range picker'
    case 'rotateAxis':
      return 'Rotate axis'
    case 'timeWindow':
      return 'Time-window picker'
    case 'followPath':
      return 'Follow-path animation'
    case 'previousStep':
      return 'Previous step'
    case 'nextStep':
      return 'Next step'
    case 'sort':
      return 'Sort'
    case 'filter':
      return 'Filter'
    case 'density':
      return 'Density picker'
    case 'range':
      return 'Range picker'
    case 'legend':
      return 'Legend toggle'
    case 'help':
      return `What is ${view}?`
    case 'shortcuts':
      return 'Keyboard shortcuts'
    default:
      return action
  }
}
